import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { S3_BUCKET_URL } from '@/constants';
import { LAYOUT_NOTES } from '@/constants/campaign-notes';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import {
  OptionItemSprite,
  OptionItemSpriteList,
} from '@/pages/Onboarding/components/OptionItemSprite/OptionItemSprite';
import { useOnboarding } from '@/pages/Onboarding/context';
import {
  OwnAiTitle,
  OwnAiTitleHighlight,
} from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { LayoutDataType } from '../../types';

import { ScreenEthnicityStyles } from './Ethnicity.styles';
import { ETHNICITY_OPTIONS } from './ETHNICITY_OPTIONS';

const getImagePath = (age: string, ethnicityId: string) => {
  const layoutNotes = webCampaign.getLayoutNotes();
  const isCompactOnboarding = layoutNotes.includes(
    LAYOUT_NOTES.TOTAL_REAL_BOYS_SCREENS,
  );

  if (isCompactOnboarding) {
    return `${S3_BUCKET_URL}/layout-own-ai-man/ethnicity/compact/sprite-${age}-${ethnicityId}.png`;
  }

  return `${S3_BUCKET_URL}/layout-own-ai-man/ethnicity/sprite-${age}-${ethnicityId}.png`;
};

export const ScreenEthnicity: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Ethnicity',
      });
    }
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const ethnicity = form.watch('ethnicity');
  const age = form.watch('age') || '18-24';

  const handleSelect = (option: OnbOptionType<LayoutDataType['ethnicity']>) => {
    form.setValue('ethnicity', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        ethnicity: option.title,
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  React.useEffect(() => {
    const layoutNotes = webCampaign.getLayoutNotes();
    const isRealBoysScreens = layoutNotes.includes(
      LAYOUT_NOTES.TOTAL_REAL_BOYS_SCREENS,
    );

    const basePath = isRealBoysScreens
      ? 'layout-own-ai-man/by_choise/real-boys'
      : 'layout-own-ai-man/by_choise';

    ETHNICITY_OPTIONS.forEach((option) => {
      preloadImage(`${S3_BUCKET_URL}/${basePath}/${age}-${option.id}-1.png`);
      preloadImage(`${S3_BUCKET_URL}/${basePath}/${age}-${option.id}-2.png`);
      preloadImage(`${S3_BUCKET_URL}/${basePath}/${age}-${option.id}-3.png`);
      preloadImage(`${S3_BUCKET_URL}/${basePath}/${age}-${option.id}-4.png`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ScreenEthnicityStyles>
      <OnboardingNavigation />
      <OwnAiTitle>
        Select his <OwnAiTitleHighlight>ethnicity</OwnAiTitleHighlight>
      </OwnAiTitle>

      <IonContent>
        <OptionItemSpriteList>
          {ETHNICITY_OPTIONS.map((option) => (
            <OptionItemSprite
              key={option.id}
              alt="ethnicity"
              isSelected={ethnicity === option.id}
              onClick={() => handleSelect(option)}
              spriteSrc={getImagePath(age, option.id)}
              title={option.title}
              TEST_ID={`ethnicity-${option.id}`}
              show
            />
          ))}
        </OptionItemSpriteList>
      </IonContent>
    </ScreenEthnicityStyles>
  );
};
